import { ColumnFilter } from '@tanstack/react-table';
import { useMemo } from 'react';

import { createFilterSectionHelper, FilterItemValue, useFilter } from '@/components-new/filter/use-filter';
import { SHARED_FILTER_SECTIONS, getMultiFilterLabel, StateFilter, PoolsFilter, PDLStatusFilter, PATypeFilter, BoardMeetingTypesFilter } from '@/components-new/filters/shared-filters';

import { TherapeuticClassFilterSchema } from '../types/filter-schema';

const filterSectionHelper = createFilterSectionHelper<TherapeuticClassFilterSchema>();

const filterSections = [
  filterSectionHelper.defineSection(
    'state',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.state;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('States', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <StateFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'pool',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.pool;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('Pool', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PoolsFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'pdlStatus',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.pdlStatus;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('PDL Statuses', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PDLStatusFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'paType',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.paType;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('PA Types', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <PATypeFilter {...ctx}/>
    }
  ),
  filterSectionHelper.defineSection(
    'boardMeetingType',
    {
      sectionLabel: () => {
        const { icon: Icon, label } = SHARED_FILTER_SECTIONS.boardMeetingType;
        return (
          <>
            <Icon/>
            {label}
          </>
        );
      },
      filterLabel: (ctx) => getMultiFilterLabel('Board Meeting Types', ctx.value, (item) => item?.label ?? ''),
      sectionContent: (ctx) => <BoardMeetingTypesFilter {...ctx}/>
    }
  )
];

const labelBasedSerializer = (filter: FilterItemValue<TherapeuticClassFilterSchema>): ColumnFilter => ({
  id: filter.key,
  value: filter.value.map(value => value.label)
});

const boardMeetingTypeSerializer = (filter: FilterItemValue<TherapeuticClassFilterSchema>): ColumnFilter => ({
  id: filter.key,
  value: filter.value.map(value => (value as any).id)
});

const serializerMap = {
  state: labelBasedSerializer,
  pool: labelBasedSerializer,
  pdlStatus: labelBasedSerializer,
  paType: labelBasedSerializer,
  boardMeetingType: boardMeetingTypeSerializer
} as const;

const serializeTableFilters = (filters: FilterItemValue<TherapeuticClassFilterSchema>[]): ColumnFilter[] => {
  return filters.map(filter => serializerMap[filter.key](filter));
};

export const useTherapeuticClassFilters = () => {
  const filter = useFilter({ filterSections });

  const tableColumnFilters = useMemo(() => serializeTableFilters(filter.values), [filter.values]);

  return {
    filter,
    tableColumnFilters
  };
};
